import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import AdunitApiService from '../../ApiServices/AnalyticsApiService/AdunitApiService';

const AddAdunitForm = ({ isOpen, onRequestClose, onSubmit, adunit = null }) => {
    const [formData, setFormData] = useState({ name: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const nameInputRef = useRef(null);

    useEffect(() => {
        if (adunit) {
            setFormData({ name: adunit.name || '' });
        } else {
            setFormData({ name: '' });
        }

        // Auto focus when modal opens
        if (isOpen && nameInputRef.current) {
            setTimeout(() => nameInputRef.current.focus(), 100);
        }
    }, [isOpen, adunit]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!formData.name.trim()) {
            setError('Adunit name is required');
            setLoading(false);
            return;
        }

        try {
            const payload = {
                name: formData.name.trim(),
                ...(adunit?.id && { id: adunit.id }),
            };

            if (adunit) {
                await AdunitApiService.update(payload.id, payload);
                toast.success('Adunit updated successfully!');
            } else {
                await AdunitApiService.create(payload);
                toast.success('Adunit created successfully!');
            }

            onSubmit();
            onRequestClose();
        } catch (err) {
            setError(err.message || 'Failed to process request.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            ariaHideApp={false}
            className="flex items-center justify-center min-h-screen p-4 z-50"
            overlayClassName="fixed inset-0 bg-black bg-opacity-40 transition-opacity"
        >
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full relative animate-fadeIn">
                {/* Close Button */}
                <button
                    onClick={onRequestClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl focus:outline-none"
                    title="Close"
                >
                    &times;
                </button>

                <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
                    {adunit ? '✏️ Edit Adunit' : '➕ Add Adunit'}
                </h2>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 mb-4 rounded">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-gray-700 font-medium mb-1">Adunit Name</label>
                        <input
                            ref={nameInputRef}
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter adunit name"
                            className="w-full border border-gray-300 px-4 py-2 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                            required
                        />
                    </div>

                    <div className="flex justify-end space-x-4 pt-4">
                        <button
                            type="button"
                            onClick={onRequestClose}
                            className="bg-gray-200 text-gray-700 px-5 py-2 rounded-md hover:bg-gray-300 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-5 py-2 rounded-md text-white transition ${
                                loading ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'
                            }`}
                        >
                            {loading ? 'Saving...' : adunit ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default AddAdunitForm;
